import * as React from 'react';
import { styled } from '@glitz/react';
import { itemKey, type ItemKeyType, ROOT_KEY } from 'webapp/shell/main-menu/helpers';
import { Level, isRoot } from './Level';
import { animate } from '@avensia/excite-ui';
import { TabsType } from 'webapp/shell/main-menu/Tabs';

const TRANSLATE_X_LEFT = -100;
const TRANSLATE_X_RIGHT = 100;

export type MenuItemsOrItemType = NordicNest.Api.MenuItemApiModel[] | NordicNest.Api.MenuItemApiModel;
type PropType = {
  current: MenuItemsOrItemType;
  setLevel?: (key: ItemKeyType) => void;
  children?: React.ReactNode;
  selectedItemKey?: string;
  selectedTab?: TabsType;
};

function transformEnterForm(isPush: boolean) {
  return `translateX(${isPush ? TRANSLATE_X_RIGHT : TRANSLATE_X_LEFT}%)`;
}

function transformLeave(isPush: boolean) {
  return `translateX(${isPush ? TRANSLATE_X_LEFT : TRANSLATE_X_RIGHT}%)`;
}

function transformEnter() {
  return 'translateX(0)';
}

const animationOptions: KeyframeAnimationOptions = {
  fill: 'forwards',
  duration: 300,
};

export default styled(function Nav(props: PropType) {
  const enterRef = React.useRef<HTMLDivElement>(null);
  const leavingRef = React.useRef<HTMLDivElement>(null);
  const { current, setLevel, children } = props;
  const previousPrimary = React.useRef(current);
  const previousChildren = React.useRef(children);

  React.useLayoutEffect(() => {
    if (previousPrimary.current !== current) {
      const isPush = isPushing(current, previousPrimary.current);
      const enteringElement = enterRef.current!;
      animate(
        enteringElement,
        [{ transform: transformEnterForm(isPush) }, { transform: transformEnter() }],
        animationOptions,
      );

      const leavingElement = leavingRef.current!;
      animate(
        leavingElement,
        [{ transform: transformEnter() }, { transform: transformLeave(isPush) }],
        animationOptions,
      );
    }
  }, [current]);

  React.useEffect(() => {
    previousPrimary.current = current;
    previousChildren.current = isRoot(current) ? children : undefined;
  }, [children, current]);

  const pushLevel = React.useCallback(
    (item: NordicNest.Api.MenuItemApiModel) => {
      setLevel?.(itemKey(item));
    },
    [setLevel],
  );
  return (
    <>
      <Entry ref={enterRef} key={levelKey(previousPrimary.current)}>
        <Level
          selectedItemKey={props.selectedItemKey}
          data={props.current}
          pushLevel={pushLevel}
          selectedTab={props.selectedTab}
        >
          {props.children}
        </Level>
      </Entry>
      {current !== previousPrimary.current && (
        <Entry ref={leavingRef}>
          <Level data={previousPrimary.current} pushLevel={pushLevel} selectedTab={props.selectedTab} />
          {previousChildren.current}
        </Entry>
      )}
    </>
  );
});

function levelKey(items: MenuItemsOrItemType) {
  return isRoot(items) ? ROOT_KEY : itemKey(items);
}

function isPushing(current: MenuItemsOrItemType, previous: MenuItemsOrItemType) {
  if (isRoot(current)) {
    return current === previous;
  }
  if (isRoot(previous)) {
    return true;
  }

  return previous.childPages && previous.childPages.length > 0 && !previous.childPages.every(item => item !== current);
}

const Entry = styled.div({
  position: 'absolute',
  width: '100%',
  height: '100%',
  top: 0,
  bottom: 0,
  display: 'flex',
  flexDirection: 'column',
  willChange: 'opacity, transform',
});
